import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Tag,
    Layout,
    Row,
    Typography,
    Modal,
    message,
    Tabs,
    Popconfirm,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import { SearchOutlined, MailOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import type { ColumnType } from "antd/es/table";
import {
    FETCH_ALL_REMINDERS,
    FETCH_SIGNED_DOCUMENTS,
    SEND_REMINDER_EMAIL,
} from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";
import { getTagColor } from "./reminder.utils";
import { isPdf } from "../document/DocumentTableComponent";
import axios from "../../services/axiosInstance";

const { Title, Link } = Typography;

const ReminderComponent = () => {
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const [data, setData] = useState<IDatabase[]>([]);
    const searchInput = useRef<InputRef>(null);

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const [emailLoadingId, setEmailLoadingId] = useState<string | null>(null);

    const today = moment().startOf("day");

    const fetchSignedUrl = async (s3Url: string) => {
        const response = await axios.get(
            `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(s3Url)}`
        );
        if (response?.data?.results) return response.data.results;
        throw new Error("Failed to fetch signed URL");
    };

    const fetchDocuments = async () => {
        const response = await axios.get(FETCH_ALL_REMINDERS);
        if (response?.data?.results) setData(response.data.results);
    };

    const sendEmailReminder = async (id: string) => {
        try {
            setEmailLoadingId(id);
            await axios.post(SEND_REMINDER_EMAIL(id), {});
            message.success("Reminder email sent successfully");
        } catch {
            message.error("Failed to send reminder email");
        } finally {
            setEmailLoadingId(null);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const handleSearch = useMemo(
        () =>
            debounce((selectedKeys, confirm, dataIndex) => {
                confirm();
                setSearchText(selectedKeys[0]);
                setSearchedColumn(dataIndex);
            }, 300),
        []
    );

    const getColumnSearchProps = (
        dataIndex: keyof IDatabase
    ): ColumnType<IDatabase> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys, confirm, dataIndex)
                    }
                    style={{ marginBottom: 8 }}
                />
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    >
                        Search
                    </Button>
                    <Button size="small" onClick={() => clearFilters?.()}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes(String(value).toLowerCase()) ?? false,
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text || ""}
                />
            ) : (
                text
            ),
    });

    /* ---------------- COLUMNS ---------------- */

    const columns = useMemo(
        () => [
            {
                title: "Type",
                dataIndex: "documentType",
                key: "documentType",
                width: 175,
                ...getColumnSearchProps("documentType"),
            },
            {
                title: "Expiry Date",
                dataIndex: "expiryDate",
                key: "expiryDate",
                render: (date: string) => {
                    if (!date) return "-";
                    const daysLeft = moment(date).diff(today, "days");

                    let color = "green";
                    if (daysLeft <= 30) color = "red";
                    else if (daysLeft <= 60) color = "orange";

                    return (
                        <Tag color={color}>
                            {moment(date).format("MMM D, YYYY")}
                        </Tag>
                    );
                },
            },
            {
                title: "Document",
                dataIndex: "s3Url",
                key: "s3Url",
                render: (s3Url: string) =>
                    s3Url ? (
                        <Link
                            onClick={async () => {
                                try {
                                    setLoadingPreview(true);
                                    const url = await fetchSignedUrl(s3Url);
                                    setSignedUrl(url);
                                    setIsPreviewOpen(true);
                                } catch {
                                    message.error("Failed to load document");
                                } finally {
                                    setLoadingPreview(false);
                                }
                            }}
                        >
                            View
                        </Link>
                    ) : (
                        "-"
                    ),
            },
            {
                title: "By",
                dataIndex: "uploadedBy",
                key: "uploadedBy",
            },
            {
                title: "Meta",
                dataIndex: "meta",
                key: "meta",
                render: (meta: Record<string, any> | string[]) => {
                    if (!meta) return "-";

                    if (typeof meta === "object" && !Array.isArray(meta)) {
                        return imageInfo(meta).map(({ key, value }) => (
                            <Tag key={key} color={getTagColor(key)}>
                                {snakeToCapitalCase(key)} : {String(value)}
                            </Tag>
                        ));
                    }

                    if (Array.isArray(meta)) {
                        return meta.map((item) => <Tag key={item}>{item}</Tag>);
                    }

                    return "-";
                },
            },
            {
                title: "Actions",
                key: "actions",
                render: (_: any, record: IDatabase) => {
                    const isRenewed = record.isRenewed;

                    return (
                        <Popconfirm
                            title={
                                isRenewed
                                    ? "Renewed documents do not require reminders"
                                    : "Send email reminder?"
                            }
                            onConfirm={() =>
                                !isRenewed && sendEmailReminder(record.documentId)
                            }
                            disabled={isRenewed}
                        >
                            <Button
                                icon={<MailOutlined />}
                                type="primary"
                                ghost
                                disabled={isRenewed}
                                loading={
                                    !isRenewed &&
                                    emailLoadingId === record.documentId
                                }
                            >
                                Email Reminder
                            </Button>
                        </Popconfirm>
                    );
                },
            },
        ],
        [searchText, searchedColumn, emailLoadingId]
    );

    /* ---------------- BUCKETS ---------------- */

    const { last30Days, last60Days, rest, expired, renewed } = useMemo(() => {
        const expired: IDatabase[] = [];
        const last30Days: IDatabase[] = [];
        const last60Days: IDatabase[] = [];
        const rest: IDatabase[] = [];
        const renewed: IDatabase[] = [];

        data.forEach((item) => {
            if (item.isRenewed) {
                renewed.push(item);
                return;
            }

            if (!item.expiryDate) {
                rest.push(item);
                return;
            }

            const diff = moment(item.expiryDate).diff(today, "days");

            if (diff < 0) expired.push(item);
            else if (diff <= 30) last30Days.push(item);
            else if (diff <= 60) last60Days.push(item);
            else rest.push(item);
        });

        return { last30Days, last60Days, rest, expired, renewed };
    }, [data, today]);

    /* ---------------- UI ---------------- */

    return (
        <Layout>
            <Modal
                open={isPreviewOpen}
                title="Document Preview"
                footer={null}
                onCancel={() => {
                    setIsPreviewOpen(false);
                    setSignedUrl(null);
                }}
                width="80%"
            >
                {loadingPreview && signedUrl ? (
                    <div style={{ textAlign: "center" }}>Loading...</div>
                ) : signedUrl && isPdf(signedUrl) ? (
                    <iframe src={signedUrl} width="100%" style={{ height: "75vh" }} />
                ) : (
                    <img
                        src={signedUrl || ""}
                        alt="Document Preview"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "75vh",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                        }}
                    />
                )}
            </Modal>

            <Row justify="space-between" align="middle">
                <Title level={2}>Expiry Reminders</Title>
            </Row>

            <Tabs defaultActiveKey="30" type="line" size="large">
                <Tabs.TabPane tab={`Expiring in 30 Days (${last30Days.length})`} key="30">
                    <Table rowKey="id" columns={columns} dataSource={last30Days} />
                </Tabs.TabPane>

                <Tabs.TabPane tab={`Expiring 31–60 Days (${last60Days.length})`} key="60">
                    <Table rowKey="id" columns={columns} dataSource={last60Days} />
                </Tabs.TabPane>

                <Tabs.TabPane tab={`Expiring Later (${rest.length})`} key="rest">
                    <Table rowKey="id" columns={columns} dataSource={rest} />
                </Tabs.TabPane>

                <Tabs.TabPane tab={`Expired (${expired.length})`} key="expired">
                    <Table rowKey="id" columns={columns} dataSource={expired} />
                </Tabs.TabPane>

                <Tabs.TabPane tab={`Renewed (${renewed.length})`} key="renewed">
                    <Table rowKey="id" columns={columns} dataSource={renewed} />
                </Tabs.TabPane>
            </Tabs>
        </Layout>
    );
};

export default ReminderComponent;
